import { useEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { Button, TextField as MuiTextField, Typography, IconButton } from '@material-ui/core';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { useFormContext, useWatch } from 'react-hook-form';
import _ from 'lodash';

import FormSection from '../../form/FormSection';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import { RowFlexLayout } from '../../layout';
import FieldEditor from './TestInstanceTemplateFieldEditor';
import useTestInstanceTemplateStyles from './TestInstanceTemplateStyles';
import { removeAtIndex, normalizeSectionsForUi } from './TestInstanceTemplateSectionHelper';

export default function TestInstanceTemplateSection({ sectionsFieldName, inputTypes = [], setConfigIsDirty }) {
    const ROW_HEADER_GAP = 8;
    const classes = useTestInstanceTemplateStyles();

    const methods = useFormContext();
    const sections = useWatch({
        control: methods.control,
        name: sectionsFieldName,
    });
    const [localSections, setLocalSections] = useState(() => normalizeSectionsForUi(sections || []));

    useEffect(() => {
        methods.register(sectionsFieldName);
    }, [methods, sectionsFieldName]);

    useEffect(() => {
        const normalizedSections = normalizeSectionsForUi(sections || []);
        setLocalSections(normalizedSections);

        if (!_.isEqual(normalizedSections, sections || [])) {
            methods.setValue(sectionsFieldName, normalizedSections, {
                shouldDirty: false,
                shouldValidate: false,
            });
        }
    }, [sections, methods, sectionsFieldName]);

    const updateSections = (nextSections) => {
        setLocalSections(nextSections);
        methods.setValue(sectionsFieldName, nextSections, { shouldDirty: true, shouldValidate: false });
        setConfigIsDirty(true);
    };

    // --- Immutable update helpers ---

    const updateSection = (sectionIndex, updater) => {
        updateSections((localSections || []).map((section, i) => (i === sectionIndex ? updater(section) : section)));
    };

    const updateRow = (sectionIndex, rowIndex, updater) => {
        updateSection(sectionIndex, (section) => ({
            ...section,
            fields: (section.fields || []).map((row, i) => (i === rowIndex ? updater(row) : row)),
        }));
    };

    const updateField = (sectionIndex, rowIndex, fieldIndex, updater) => {
        updateRow(sectionIndex, rowIndex, (row) => ({
            ...row,
            fields: (row.fields || []).map((field, i) => (i === fieldIndex ? updater(field) : field)),
        }));
    };

    // --- Handlers ---

    const handleAddSection = () => {
        updateSections([
            ...(localSections || []),
            { [INTERNAL_ID_FIELD]: _.uniqueId('section_'), title: '', fields: [] },
        ]);
    };

    const handleRemoveSection = (sectionIndex) => {
        updateSections(removeAtIndex(localSections || [], sectionIndex));
    };

    const handleSectionTitleChange = (sectionIndex, value) => {
        updateSection(sectionIndex, (section) => ({ ...section, title: value }));
    };

    const handleAddRow = (sectionIndex) => {
        updateSection(sectionIndex, (section) => ({
            ...section,
            fields: [...(section.fields || []), { [INTERNAL_ID_FIELD]: _.uniqueId('row_'), fields: [] }],
        }));
    };

    const handleRemoveRow = (sectionIndex, rowIndex) => {
        updateSection(sectionIndex, (section) => ({
            ...section,
            fields: removeAtIndex(section.fields || [], rowIndex),
        }));
    };

    const handleAddField = (sectionIndex, rowIndex) => {
        updateRow(sectionIndex, rowIndex, (row) => ({
            ...row,
            fields: [
                ...(row.fields || []),
                { [INTERNAL_ID_FIELD]: _.uniqueId('field_'), required: true, maxlength: 50, type: 'text', title: '' },
            ],
        }));
    };

    const handleRemoveField = (sectionIndex, rowIndex, fieldIndex) => {
        updateRow(sectionIndex, rowIndex, (row) => ({
            ...row,
            fields: removeAtIndex(row.fields || [], fieldIndex),
        }));
    };

    const handleFieldChange = (sectionIndex, rowIndex, fieldIndex, key, value) => {
        updateField(sectionIndex, rowIndex, fieldIndex, (field) => ({ ...field, [key]: value }));
    };

    const handleFieldTypeChange = (sectionIndex, rowIndex, fieldIndex, nextType) => {
        updateField(sectionIndex, rowIndex, fieldIndex, (field) => {
            const updated = { ...field, type: nextType };

            if (nextType === 'boolean') {
                updated.default = !!field?.default;
            }

            if (nextType === 'select' || nextType === 'radio') {
                if (!_.isArray(updated.options)) {
                    updated.options = [];
                }
                if (!_.some(updated.options, (option) => option?.value === updated.default)) {
                    updated.default = '';
                }
            }

            if (nextType !== 'select' && nextType !== 'radio') {
                delete updated.options;
            }

            return updated;
        });
    };

    const handleAddOption = (sectionIndex, rowIndex, fieldIndex) => {
        updateField(sectionIndex, rowIndex, fieldIndex, (field) => ({
            ...field,
            options: [...(field.options || []), { [INTERNAL_ID_FIELD]: _.uniqueId('option_'), name: '', value: '' }],
        }));
    };

    const handleRemoveOption = (sectionIndex, rowIndex, fieldIndex, optionIndex) => {
        updateField(sectionIndex, rowIndex, fieldIndex, (field) => ({
            ...field,
            options: removeAtIndex(field.options || [], optionIndex),
        }));
    };

    const handleOptionChange = (sectionIndex, rowIndex, fieldIndex, optionIndex, key, value) => {
        updateField(sectionIndex, rowIndex, fieldIndex, (field) => ({
            ...field,
            options: (field.options || []).map((option, i) =>
                i === optionIndex ? { ...option, [key]: value } : option
            ),
        }));
    };

    return (
        <FormSection title={<Trans>Test Instance Template (optional)</Trans>} addSpacer>
            {(localSections || []).map((section, sectionIndex) => (
                <div key={section?.[INTERNAL_ID_FIELD]} className={classes.sectionContainer}>
                    <RowFlexLayout vBottom gridGap={8} marginBottom={2}>
                        <MuiTextField
                            label={t`Section header for this group of fields.`}
                            value={section?.title || ''}
                            onChange={(event) => handleSectionTitleChange(sectionIndex, event.target.value)}
                            inputProps={{ maxLength: 150 }}
                            fullWidth
                            className={classes.sectionTitleField}
                        />
                        <IconButton
                            className={classes.primaryIconButton}
                            aria-label={t`Remove section`}
                            onClick={() => handleRemoveSection(sectionIndex)}
                            size="small"
                        >
                            <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                    </RowFlexLayout>

                    {(section?.fields || []).map((row, rowIndex) => {
                        const rowFields = row?.fields || [];
                        const hasFields = rowFields.length > 0;

                        return (
                            <div key={row?.[INTERNAL_ID_FIELD]} className={classes.rowContainer}>
                                <RowFlexLayout vCenter gridGap={ROW_HEADER_GAP} marginBottom={hasFields ? 1 : 0}>
                                    <IconButton
                                        className={classes.primaryIconButton}
                                        aria-label={t`Remove row`}
                                        onClick={() => handleRemoveRow(sectionIndex, rowIndex)}
                                        size="small"
                                    >
                                        <RemoveCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="subtitle2">
                                        <Trans>Field Row </Trans> {`#${rowIndex + 1}`}
                                    </Typography>
                                    {!hasFields && (
                                        <Button
                                            size="small"
                                            color="primary"
                                            onClick={() => handleAddField(sectionIndex, rowIndex)}
                                        >
                                            <Trans>Add a field</Trans>
                                        </Button>
                                    )}
                                </RowFlexLayout>

                                {rowFields.map((field, fieldIndex) => (
                                    <FieldEditor
                                        key={field?.[INTERNAL_ID_FIELD]}
                                        sectionIndex={sectionIndex}
                                        rowIndex={rowIndex}
                                        field={field}
                                        fieldIndex={fieldIndex}
                                        inputTypes={inputTypes}
                                        onFieldChange={handleFieldChange}
                                        onFieldTypeChange={handleFieldTypeChange}
                                        onRemoveField={handleRemoveField}
                                        onOptionChange={handleOptionChange}
                                        onRemoveOption={handleRemoveOption}
                                        onAddOption={handleAddOption}
                                    />
                                ))}

                                {hasFields && (
                                    <Button
                                        size="small"
                                        color="primary"
                                        className={classes.fieldActionButton}
                                        onClick={() => handleAddField(sectionIndex, rowIndex)}
                                    >
                                        <Trans>Add a field</Trans>
                                    </Button>
                                )}
                            </div>
                        );
                    })}

                    <Button
                        size="small"
                        color="primary"
                        className={classes.rowActionButton}
                        onClick={() => handleAddRow(sectionIndex)}
                    >
                        <Trans>Add a field row</Trans>
                    </Button>
                </div>
            ))}

            <Button size="small" color="primary" className={classes.addSectionButton} onClick={handleAddSection}>
                <Trans>Add a section</Trans>
            </Button>
        </FormSection>
    );
}
